import { useState, useRef, useEffect } from 'react';
import {
  X, Download, Calendar, Clock, MapPin, User, Phone, Mail,
  Shield, Wrench, Camera, CheckSquare, MessageSquare,
  Receipt, DollarSign, FileText, Briefcase, Loader2, Printer
} from 'lucide-react';
import { paymentBatchService } from '../../../services/paymentBatchService';
import { cn } from '../../../utils/cn';
import { useUserPreferences } from '../../../contexts/UserPreferencesContext';
import {
  calculateInstanceFinancials,
  calculateContractorBillableHours,
  formatCurrency,
  formatHoursMinutes
} from '../../../utils/earningsCalculations';
import { downloadPdf, printContent } from '../../../utils/pdfUtils';

// Days of the week for schedule display
const DAYS = [
  { key: 'mon', label: 'M', fullLabel: 'Monday' },
  { key: 'tue', label: 'T', fullLabel: 'Tuesday' },
  { key: 'wed', label: 'W', fullLabel: 'Wednesday' },
  { key: 'thu', label: 'T', fullLabel: 'Thursday' },
  { key: 'fri', label: 'F', fullLabel: 'Friday' },
  { key: 'sat', label: 'S', fullLabel: 'Saturday' },
  { key: 'sun', label: 'S', fullLabel: 'Sunday' }
];

// Format time from 24h to 12h or 24h based on preference
const formatTime = (time, format = '12h') => {
  if (!time) return null;
  const [hours, minutes] = time.split(':');
  const h = parseInt(hours, 10);

  if (format === '24h') {
    return `${hours}:${minutes}`;
  }

  const suffix = h >= 12 ? 'PM' : 'AM';
  const displayHour = h % 12 || 12;
  return `${displayHour}:${minutes} ${suffix}`;
};

// Format date based on preference
const formatDate = (dateStr, format = 'DD/MM/YYYY') => {
  if (!dateStr) return 'Not set';
  const date = new Date(dateStr);

  const day = date.getDate();
  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const weekday = weekdays[date.getDay()];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = months[date.getMonth()];
  const year = date.getFullYear();

  switch (format) {
    case 'MM/DD/YYYY':
      return `${weekday}, ${month} ${day}, ${year}`;
    case 'YYYY-MM-DD':
      return `${weekday}, ${year} ${month} ${day}`;
    case 'DD/MM/YYYY':
    default:
      return `${weekday}, ${day} ${month} ${year}`;
  }
};

// Get status badge color
const getStatusColor = (status) => {
  const colors = {
    created: 'bg-gray-100 text-gray-700',
    assigned: 'bg-blue-100 text-blue-700',
    in_progress: 'bg-yellow-100 text-yellow-700',
    completed: 'bg-green-100 text-green-700',
    approved: 'bg-emerald-100 text-emerald-700',
    paid: 'bg-purple-100 text-purple-700',
    cancelled: 'bg-red-100 text-red-700',
    submitted: 'bg-orange-100 text-orange-700'
  };
  return colors[status] || 'bg-gray-100 text-gray-700';
};

// Get priority color
const getPriorityColor = (priority) => {
  const colors = {
    high: 'bg-red-100 text-red-700',
    medium: 'bg-yellow-100 text-yellow-700',
    low: 'bg-green-100 text-green-700'
  };
  return colors[priority?.toLowerCase()] || 'bg-gray-100 text-gray-700';
};

// Week circles component for recurring schedule display
const WeekCircles = ({ schedule }) => {
  const activeDays = schedule?.days || [];
  const pattern = schedule?.pattern || 'fixed';
  const fixedTime = schedule?.time;
  const customTimes = schedule?.customTimes || {};

  const getTimeForDay = (dayKey) => {
    if (pattern === 'custom') {
      return customTimes[dayKey] || null;
    }
    return fixedTime || null;
  };

  // If no days are selected, show "Custom Schedule" message
  if (!activeDays || activeDays.length === 0) {
    return (
      <p className="text-sm text-gray-600 italic">Custom schedule (varies by instance)</p>
    );
  }

  return (
    <div className="flex items-center gap-1">
      {DAYS.map((day) => {
        const isActive = activeDays.includes(day.key);
        const time = getTimeForDay(day.key);
        return (
          <div
            key={day.key}
            className={cn(
              'w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium',
              isActive ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-400'
            )}
            title={isActive ? `${day.fullLabel}${time ? ` at ${formatTime(time)}` : ''}` : day.fullLabel}
          >
            {day.label}
          </div>
        );
      })}
    </div>
  );
};

// Format full datetime for display
const formatFullDateTime = (dateTimeStr, dateFormat = 'DD/MM/YYYY', timeFormat = '12h') => {
  if (!dateTimeStr) return 'Not recorded';
  try {
    const dt = new Date(dateTimeStr);
    const day = dt.getDate();
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[dt.getMonth()];
    const year = dt.getFullYear();
    const time = dt.toLocaleTimeString('en-AU', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: timeFormat === '12h'
    });

    switch (dateFormat) {
      case 'MM/DD/YYYY':
        return `${month} ${day}, ${year} at ${time}`;
      case 'YYYY-MM-DD':
        return `${year} ${month} ${day} at ${time}`;
      case 'DD/MM/YYYY':
      default:
        return `${day} ${month} ${year} at ${time}`;
    }
  } catch {
    return dateTimeStr;
  }
};

export default function ContractorInvoice({
  job,
  instance,
  company,
  contractor,
  onClose,
  timesheet,
  photos,
  workNotes,
  checklistData,
  questionnaireResponses,
  companyGstRate = 10
}) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [paymentBatch, setPaymentBatch] = useState(null);
  const [loadingBatch, setLoadingBatch] = useState(false);
  const invoiceRef = useRef(null);
  const { preferences } = useUserPreferences();

  if (!job) return null;

  const isRecurring = job?.jobType === 'recurring';
  const isInstanceView = !!instance;
  const schedule = job?.recurringSchedule;

  // Check if report data is available
  const hasReportData = !!(timesheet || instance?.clock_in_time || instance?.clock_out_time || workNotes || checklistData || questionnaireResponses || photos?.length > 0);

  // Use job display ID as the invoice reference
  const invoiceNumber = isInstanceView
    ? `${job?.jobDisplayId || 'JOB'}-#${instance?.instance_number || 1}`
    : job?.jobDisplayId || 'JOB';

  // Company initials for logo placeholder
  const companyInitials = company?.company_name
    ?.split(' ')
    ?.map(w => w[0])
    ?.join('')
    ?.substring(0, 2)
    ?.toUpperCase() || 'CO';

  // Format company address
  const formatCompanyAddress = () => {
    const parts = [];
    if (company?.address_line1) parts.push(company.address_line1);
    if (company?.address_line2) parts.push(company.address_line2);
    const cityStatePostcode = [company?.city, company?.state, company?.postcode].filter(Boolean).join(' ');
    if (cityStatePostcode) parts.push(cityStatePostcode);
    if (company?.country && company?.country !== 'Australia') parts.push(company.country);
    return parts.length > 0 ? parts.join(', ') : null;
  };

  const companyAddress = formatCompanyAddress();
  const companyPhone = company?.phone;
  const companyEmail = company?.contact_email;

  // Separate photos by type
  const beforePhotos = photos?.filter(p => p.photo_type === 'before') || [];
  const afterPhotos = photos?.filter(p => p.photo_type === 'after') || [];

  // Get estimated and actual hours
  const estimatedHours = parseFloat(job?.estimatedHours || job?.estimated_hours) || 0;
  const actualHours = parseFloat(timesheet?.total_hours || 0);

  // ===== FINANCIAL CALCULATIONS =====
  const jobForCalc = {
    payment_type: job?.paymentType || job?.payment_type || 'hourly',
    payment_amount: job?.paymentAmount || job?.payment_amount,
    contractor_include_gst: job?.contractorIncludeGst ?? job?.contractor_include_gst ?? false,
    contractor_gst_type: job?.contractorGstType || job?.contractor_gst_type || 'exclusive',
    estimated_hours: estimatedHours
  };

  const instanceForCalc = {
    timesheet: timesheet ? [timesheet] : instance?.timesheet,
    overtime_request: instance?.overtime_request
  };

  // Calculate financials
  const financials = calculateInstanceFinancials(instanceForCalc, jobForCalc, companyGstRate);

  // Contractor payment status - check both timesheet and instance, prefer 'paid' if either has it
  // The instance status is updated to 'paid' when payment is marked, but timesheet might still show 'approved'
  const timesheetStatus = timesheet?.status;
  const instanceStatus = instance?.status;
  const contractorPaymentStatus = (timesheetStatus === 'paid' || instanceStatus === 'paid')
    ? 'paid'
    : (timesheetStatus || instanceStatus);
  const isPaid = contractorPaymentStatus === 'paid';

  // Calculate total contractor pay
  const totalContractorPay = (financials.payoutToContractor || 0) +
    (financials.hasOvertime && financials.overtimeApproved ? (financials.overtimePayoutToContractor || 0) : 0);

  // Load payment batch data when job is paid
  useEffect(() => {
    const loadPaymentBatch = async () => {
      if (!isPaid || !instance?.id) return;

      setLoadingBatch(true);
      try {
        const batchData = await paymentBatchService.getBatchForJobInstance(instance.id);
        setPaymentBatch(batchData);
      } catch (err) {
        console.error('Error loading payment batch:', err);
      } finally {
        setLoadingBatch(false);
      }
    };

    loadPaymentBatch();
  }, [isPaid, instance?.id]);

  // Handle PDF download using server-side Puppeteer rendering
  const handleDownloadPDF = async () => {
    if (!invoiceRef.current) return;

    setIsDownloading(true);
    await downloadPdf({
      contentRef: invoiceRef,
      filename: `Invoice-${invoiceNumber}`,
      onSuccess: () => {
        // PDF downloaded successfully
      },
      onError: (message) => {
        console.error('Failed to generate PDF:', message);
        alert('Failed to generate PDF. Please try again.');
      },
      onDevFallback: () => {
        alert('PDF download requires deployment. Use Print → Save as PDF for now.');
      }
    });
    setIsDownloading(false);
  };

  // Handle print using iframe for clean output
  const handlePrint = async () => {
    if (!invoiceRef.current) return;

    setIsPrinting(true);
    await printContent({
      contentRef: invoiceRef,
      onComplete: () => {
        setIsPrinting(false);
      }
    });
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 pt-20">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50 rounded-t-xl print:hidden">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Receipt className="w-5 h-5 text-orange-500" />
            Invoice
          </h2>

          <div className="flex items-center gap-3">
            <button
              onClick={handleDownloadPDF}
              disabled={isDownloading}
              className={cn(
                "flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg transition-colors",
                isDownloading ? "opacity-50 cursor-not-allowed" : "hover:bg-orange-600"
              )}
            >
              <Download className="w-4 h-4" />
              {isDownloading ? 'Generating...' : 'Download PDF'}
            </button>
            <button
              onClick={handlePrint}
              disabled={isPrinting}
              className={cn(
                "flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg transition-colors",
                isPrinting ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-200"
              )}
            >
              <Printer className="w-4 h-4" />
              {isPrinting ? 'Preparing...' : 'Print'}
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Invoice Content */}
        <div className="flex-1 overflow-auto p-6">
          <div ref={invoiceRef} className="bg-white" style={{ minWidth: '700px' }}>
            {/* Invoice Header */}
            <div className="flex justify-between items-start mb-8 pb-6 border-b-2 border-orange-500">
              <div className="flex items-center gap-4">
                {company?.logo_url ? (
                  <img
                    src={company.logo_url}
                    alt={company?.company_name || 'Company'}
                    className="w-16 h-16 rounded-lg object-cover shadow-md"
                    crossOrigin="anonymous"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white text-xl font-bold shadow-md">
                    {companyInitials}
                  </div>
                )}
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">{company?.company_name || 'Company Name'}</h1>
                  {companyAddress && <p className="text-sm text-gray-500 mt-1">{companyAddress}</p>}
                  <div className="flex items-center gap-4 mt-1">
                    {companyPhone && (
                      <p className="text-xs text-gray-400 flex items-center gap-1">
                        <Phone className="w-3 h-3" /> {companyPhone}
                      </p>
                    )}
                    {companyEmail && (
                      <p className="text-xs text-gray-400 flex items-center gap-1">
                        <Mail className="w-3 h-3" /> {companyEmail}
                      </p>
                    )}
                  </div>
                  {company?.abn && (
                    <p className="text-xs text-gray-500 mt-1">ABN: {company.abn}</p>
                  )}
                </div>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-orange-500 mb-2">INVOICE</p>
                <p className="text-lg font-mono text-gray-700">{invoiceNumber}</p>
                <p className="text-sm text-gray-500 mt-2">
                  Generated: {new Date().toLocaleDateString('en-AU', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            </div>

            {/* Contractor Details & Job Summary */}
            <div className="grid grid-cols-2 gap-8 mb-8 avoid-break" style={{ pageBreakInside: 'avoid' }}>
              <div>
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Contractor Details</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  {contractor ? (
                    <>
                      <p className="font-semibold text-gray-900">{contractor.full_name || 'N/A'}</p>
                      {contractor.email && (
                        <p className="text-sm text-gray-600 mt-1 flex items-center gap-2">
                          <Mail className="w-3 h-3" /> {contractor.email}
                        </p>
                      )}
                      {contractor.abn && (
                        <p className="text-sm text-gray-600 mt-1">
                          <span className="text-gray-400">ABN:</span> {contractor.abn}
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-gray-500">Contractor details not available</p>
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Job Summary</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex flex-wrap gap-2 mb-3">
                    <span className={cn('px-2 py-1 rounded text-xs font-medium', isRecurring ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700')}>
                      {isRecurring ? 'Recurring' : 'One-Time'}
                    </span>
                    <span className={cn('px-2 py-1 rounded text-xs font-medium', getPriorityColor(job?.priority))}>
                      {job?.priority || 'Normal'} Priority
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Created:</span> {formatDate(job?.createdAt, preferences.date_format)}
                  </p>
                </div>
              </div>
            </div>

            {/* Job Details */}
            <div className="mb-8 avoid-break" style={{ pageBreakInside: 'avoid' }}>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Job Details</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">{job?.jobName || 'Untitled Job'}</h4>
                {job?.description && <p className="text-sm text-gray-600 mb-4">{job.description}</p>}

                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 text-orange-500 mt-0.5" />
                    <div>
                      <p className="text-xs font-medium text-gray-400 uppercase">Location</p>
                      <p className="text-sm text-gray-900">{job?.jobSite?.address || job?.locationAddress || 'Not specified'}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Calendar className="w-4 h-4 text-orange-500 mt-0.5" />
                    <div>
                      <p className="text-xs font-medium text-gray-400 uppercase">Schedule</p>
                      {isInstanceView ? (
                        // For instance view, show the specific instance date/time
                        <p className="text-sm text-gray-900">
                          {formatDate(instance?.scheduled_date, preferences.date_format)}
                          {instance?.scheduled_time && ` at ${formatTime(instance.scheduled_time, preferences.time_format)}`}
                        </p>
                      ) : isRecurring ? (
                        // For recurring jobs without instance, show the schedule pattern
                        <div className="mt-1">
                          <WeekCircles schedule={schedule} />
                        </div>
                      ) : (
                        // For one-time jobs, show the job scheduled date
                        <p className="text-sm text-gray-900">
                          {formatDate(job?.scheduledDate, preferences.date_format)}
                          {job?.scheduledTime && ` at ${formatTime(job.scheduledTime, preferences.time_format)}`}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <User className="w-4 h-4 text-orange-500 mt-0.5" />
                    <div>
                      <p className="text-xs font-medium text-gray-400 uppercase">Contractor</p>
                      <p className="text-sm text-gray-900">
                        {isInstanceView
                          ? (instance?.contractor?.full_name || instance?.contractor?.fullName || contractor?.full_name || 'N/A')
                          : (job?.assignedContractor?.fullName || contractor?.full_name || 'N/A')}
                      </p>
                    </div>
                  </div>

                  {estimatedHours > 0 && (
                    <div className="flex items-start gap-3">
                      <Clock className="w-4 h-4 text-orange-500 mt-0.5" />
                      <div>
                        <p className="text-xs font-medium text-gray-400 uppercase">Estimated Duration</p>
                        <p className="text-sm text-gray-900">{formatHoursMinutes(estimatedHours)}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Safety & Equipment */}
            {(job?.safetyRequirements || job?.equipmentList) && (
              <div className="grid grid-cols-2 gap-6 mb-8">
                {job?.safetyRequirements && (
                  <div>
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                      <Shield className="w-4 h-4" /> Safety Requirements
                    </h3>
                    <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                      <p className="text-sm text-gray-700">{job.safetyRequirements}</p>
                    </div>
                  </div>
                )}
                {job?.equipmentList && (
                  <div>
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                      <Wrench className="w-4 h-4" /> Equipment Needed
                    </h3>
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                      <p className="text-sm text-gray-700">{job.equipmentList}</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Other Details */}
            {job?.otherDetails && (
              <div className="mb-8">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" /> Additional Information
                </h3>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{job.otherDetails}</p>
                </div>
              </div>
            )}

            {/* Instance Details */}
            {isInstanceView && instance && (
              <div className="mb-8 avoid-break" style={{ pageBreakInside: 'avoid' }}>
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Instance Details</h3>
                <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-gray-500 uppercase">ID:</span>
                      <span className="text-sm font-bold text-orange-600 whitespace-nowrap">{job?.jobDisplayId || 'JOB'}-#{instance.instance_number}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-3.5 h-3.5 text-gray-400" />
                      <span className="text-xs text-gray-500">Scheduled:</span>
                      <span className="text-sm text-gray-900">{formatDate(instance.scheduled_date, preferences.date_format)}</span>
                    </div>
                    {instance.scheduled_time && (
                      <div className="flex items-center gap-2">
                        <Clock className="w-3.5 h-3.5 text-gray-400" />
                        <span className="text-xs text-gray-500">Start Time:</span>
                        <span className="text-sm text-gray-900">{formatTime(instance.scheduled_time, preferences.time_format)}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs text-gray-500">Status:</span>
                      <span className={cn('px-2 py-0.5 rounded text-xs font-medium', getStatusColor(contractorPaymentStatus))}>
                        {(contractorPaymentStatus || 'pending').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Submitted Report Details */}
            {isInstanceView && hasReportData && (
              <div className="mb-8">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Submitted Report Details</h3>
                <div className="space-y-4">
                  {/* Timesheet */}
                  {(timesheet || instance?.clock_in_time) && (
                    <div className="bg-green-50 rounded-lg p-4 border border-green-200 avoid-break" style={{ pageBreakInside: 'avoid' }}>
                      <h4 className="text-sm font-semibold text-green-800 mb-3 flex items-center gap-2">
                        <Clock className="w-4 h-4" /> Timesheet Details
                      </h4>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <p className="text-xs font-medium text-gray-400 uppercase">Clock In</p>
                          <p className="text-sm text-gray-900 font-medium">
                            {formatFullDateTime(timesheet?.clock_in_time || instance?.clock_in_time, preferences.date_format, preferences.time_format)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-400 uppercase">Clock Out</p>
                          <p className="text-sm text-gray-900 font-medium">
                            {formatFullDateTime(timesheet?.clock_out_time || instance?.clock_out_time, preferences.date_format, preferences.time_format)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-400 uppercase">Total Duration</p>
                          <p className="text-sm text-gray-900 font-medium">
                            {timesheet?.total_hours ? formatHoursMinutes(parseFloat(timesheet.total_hours)) : 'N/A'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Before Photos */}
                  <div className="photo-section bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <h4 className="text-sm font-semibold text-blue-800 mb-3 flex items-center gap-2">
                      <Camera className="w-4 h-4" /> Before Photos {beforePhotos.length > 0 && `(${beforePhotos.length})`}
                    </h4>
                    {beforePhotos.length > 0 ? (
                      <div className="grid grid-cols-4 gap-3">
                        {beforePhotos.map((photo, idx) => (
                          <div key={photo.id || idx} className="relative aspect-square bg-gray-200 rounded-lg overflow-hidden">
                            {(photo.url || photo.storage_path) ? (
                              <img src={photo.url || photo.storage_path} alt={`Before ${idx + 1}`} className="w-full h-full object-cover" crossOrigin="anonymous" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-400"><Camera className="w-8 h-8" /></div>
                            )}
                            <span className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded text-xs font-medium bg-blue-500 text-white">Before</span>
                          </div>
                        ))}
                      </div>
                    ) : job?.photosRequiredBefore ? (
                      <p className="text-sm text-orange-600 italic">Not submitted</p>
                    ) : (
                      <p className="text-sm text-gray-500 italic">Not required</p>
                    )}
                  </div>

                  {/* After Photos */}
                  <div className="photo-section bg-green-50 rounded-lg p-4 border border-green-200">
                    <h4 className="text-sm font-semibold text-green-800 mb-3 flex items-center gap-2">
                      <Camera className="w-4 h-4" /> After Photos {afterPhotos.length > 0 && `(${afterPhotos.length})`}
                    </h4>
                    {afterPhotos.length > 0 ? (
                      <div className="grid grid-cols-4 gap-3">
                        {afterPhotos.map((photo, idx) => (
                          <div key={photo.id || idx} className="relative aspect-square bg-gray-200 rounded-lg overflow-hidden">
                            {(photo.url || photo.storage_path) ? (
                              <img src={photo.url || photo.storage_path} alt={`After ${idx + 1}`} className="w-full h-full object-cover" crossOrigin="anonymous" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-400"><Camera className="w-8 h-8" /></div>
                            )}
                            <span className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded text-xs font-medium bg-green-500 text-white">After</span>
                          </div>
                        ))}
                      </div>
                    ) : job?.photosRequiredAfter ? (
                      <p className="text-sm text-orange-600 italic">Not submitted</p>
                    ) : (
                      <p className="text-sm text-gray-500 italic">Not required</p>
                    )}
                  </div>

                  {/* Work Notes */}
                  <div className="bg-purple-50 rounded-lg p-4 border border-purple-200 avoid-break" style={{ pageBreakInside: 'avoid' }}>
                    <h4 className="text-sm font-semibold text-purple-800 mb-3 flex items-center gap-2">
                      <MessageSquare className="w-4 h-4" /> Work Notes
                    </h4>
                    {(workNotes || instance?.work_notes) ? (
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{workNotes || instance?.work_notes}</p>
                    ) : (
                      <p className="text-sm text-gray-500 italic">No notes provided</p>
                    )}
                  </div>

                  {/* Checklist */}
                  <div className="bg-teal-50 rounded-lg p-4 border border-teal-200 avoid-break" style={{ pageBreakInside: 'avoid' }}>
                    <h4 className="text-sm font-semibold text-teal-800 mb-3 flex items-center gap-2">
                      <CheckSquare className="w-4 h-4" /> Checklist
                    </h4>
                    {(() => {
                      const data = checklistData || instance?.checklist_data;
                      const items = data ? (Array.isArray(data) ? data : (data?.items || Object.entries(data || {}).map(([key, value]) => ({ label: key, checked: value })))) : [];
                      const hasData = items && items.length > 0;

                      if (hasData) {
                        return (
                          <div className="space-y-2">
                            {items.map((item, idx) => (
                              <div key={idx} className="flex items-center gap-2">
                                <span className={cn(
                                  'w-5 h-5 rounded flex items-center justify-center text-xs',
                                  (item.checked || item.completed) ? 'bg-teal-500 text-white' : 'bg-gray-200 text-gray-400'
                                )}>
                                  {(item.checked || item.completed) ? '✓' : ''}
                                </span>
                                <span className={cn('text-sm', (item.checked || item.completed) ? 'text-gray-900' : 'text-gray-500')}>
                                  {item.label || item.text || item.name || `Item ${idx + 1}`}
                                </span>
                              </div>
                            ))}
                          </div>
                        );
                      } else {
                        // No checklist data available
                        return <p className="text-sm text-gray-500 italic">Not applicable</p>;
                      }
                    })()}
                  </div>

                  {/* Questionnaire */}
                  <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200 avoid-break" style={{ pageBreakInside: 'avoid' }}>
                    <h4 className="text-sm font-semibold text-indigo-800 mb-3 flex items-center gap-2">
                      <FileText className="w-4 h-4" /> Questionnaire Responses
                    </h4>
                    {(() => {
                      const data = questionnaireResponses || instance?.questionnaire_responses;
                      const items = data ? (Array.isArray(data) ? data : Object.entries(data || {}).map(([question, answer]) => ({ question, answer }))) : [];
                      const hasData = items && items.length > 0;

                      if (hasData) {
                        return (
                          <div className="space-y-3">
                            {items.map((item, idx) => (
                              <div key={idx} className="border-b border-indigo-200 pb-2 last:border-0 last:pb-0">
                                <p className="text-xs font-medium text-indigo-600 uppercase">{item.question || item.label || `Question ${idx + 1}`}</p>
                                <p className="text-sm text-gray-900 mt-1">{item.answer || item.response || item.value || 'No response'}</p>
                              </div>
                            ))}
                          </div>
                        );
                      } else if (job?.questionnaireTemplate && job.questionnaireTemplate.length > 0) {
                        return <p className="text-sm text-orange-600 italic">Not submitted</p>;
                      } else {
                        return <p className="text-sm text-gray-500 italic">Not applicable</p>;
                      }
                    })()}
                  </div>
                </div>
              </div>
            )}

            {/* ===== FINANCIAL SECTION - CONTRACTOR EARNINGS ===== */}
            {isInstanceView && hasReportData && (
              <div className="mb-8">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" /> Earnings Details
                </h3>

                <div className="space-y-4">
                  {/* Contractor Earnings Section */}
                  <div className="financial-section bg-green-50 rounded-lg p-4 border border-green-200">
                    <h4 className="text-sm font-semibold text-green-800 mb-3">Your Earnings</h4>
                    <table className="w-full text-sm">
                      <tbody>
                        <tr>
                          <td className="py-1 text-gray-600">Billable Hours</td>
                          <td className="py-1 text-right text-gray-900">{financials.contractorBillableHours || 0} hrs</td>
                        </tr>
                        <tr>
                          <td className="py-1 text-gray-600">Rate</td>
                          <td className="py-1 text-right text-gray-900">
                            {jobForCalc.payment_type === 'hourly'
                              ? `${formatCurrency(jobForCalc.payment_amount)}/hr`
                              : `${formatCurrency(jobForCalc.payment_amount)} (fixed)`}
                          </td>
                        </tr>
                        <tr className="border-t border-green-200">
                          <td className="py-1 text-gray-700 font-medium">Base Pay</td>
                          <td className="py-1 text-right text-gray-900 font-medium">
                            {formatCurrency(financials.payoutToContractor - (financials.gstOnPayout || 0))}
                          </td>
                        </tr>
                        {financials.payoutHasGst && financials.gstOnPayout > 0 && (
                          <tr>
                            <td className="py-1 text-gray-600">GST ({companyGstRate}%)</td>
                            <td className="py-1 text-right text-gray-900">{formatCurrency(financials.gstOnPayout)}</td>
                          </tr>
                        )}

                        {/* Overtime Pay */}
                        {financials.hasOvertime && financials.overtimeApproved && financials.overtimePayoutToContractor > 0 && (
                          <>
                            <tr className="border-t border-green-200">
                              <td className="py-1 text-gray-700 font-medium" colSpan={2}>Overtime Pay (Approved)</td>
                            </tr>
                            <tr>
                              <td className="py-1 text-gray-600 pl-4">Overtime Hours</td>
                              <td className="py-1 text-right text-gray-900">{financials.contractorOvertimeHours} hrs</td>
                            </tr>
                            <tr>
                              <td className="py-1 text-gray-600 pl-4">Overtime Amount</td>
                              <td className="py-1 text-right text-gray-900">
                                {formatCurrency(financials.overtimePayoutToContractor - (financials.gstOnOvertimePayout || 0))}
                              </td>
                            </tr>
                            {financials.payoutHasGst && financials.gstOnOvertimePayout > 0 && (
                              <tr>
                                <td className="py-1 text-gray-600 pl-4">GST on Overtime</td>
                                <td className="py-1 text-right text-gray-900">{formatCurrency(financials.gstOnOvertimePayout)}</td>
                              </tr>
                            )}
                          </>
                        )}
                        {financials.hasOvertime && financials.overtimePending && (
                          <tr className="border-t border-green-200">
                            <td className="py-1 text-yellow-700" colSpan={2}>Overtime Pending Approval</td>
                          </tr>
                        )}

                        {/* Total Earnings */}
                        <tr className="border-t-2 border-green-300 bg-green-100">
                          <td className="py-2 text-green-900 font-bold">TOTAL EARNINGS</td>
                          <td className="py-2 text-right text-green-900 font-bold text-lg">{formatCurrency(totalContractorPay)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Payment Status */}
                  <div className="financial-section bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <h4 className="text-sm font-semibold text-gray-800 mb-3">Payment Status</h4>
                    <div className="flex flex-wrap items-center gap-3">
                      <span className={cn(
                        'inline-flex items-center px-3 py-1 rounded-full text-sm font-medium',
                        isPaid ? 'bg-purple-100 text-purple-700' :
                        contractorPaymentStatus === 'approved' ? 'bg-green-100 text-green-700' :
                        'bg-orange-100 text-orange-700'
                      )}>
                        {isPaid ? 'Paid' : contractorPaymentStatus === 'approved' ? 'Approved' : 'Pending'}
                      </span>

                      {/* Contractor confirmation status */}
                      {isPaid && (
                        <span className={cn(
                          'inline-flex items-center px-3 py-1 rounded-full text-sm font-medium',
                          timesheet?.contractor_confirmed_received
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-600'
                        )}>
                          {timesheet?.contractor_confirmed_received ? '✓ Payment Received' : 'Not Confirmed'}
                        </span>
                      )}
                    </div>

                    {/* Confirmation details */}
                    {isPaid && timesheet?.contractor_confirmed_received && timesheet?.contractor_confirmed_at && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <p className="text-xs text-gray-500">
                          Confirmed on: {new Date(timesheet.contractor_confirmed_at).toLocaleString('en-AU', {
                            dateStyle: 'medium',
                            timeStyle: 'short'
                          })}
                        </p>
                        {timesheet?.contractor_confirmation_note && (
                          <p className="text-xs text-gray-500 mt-1">
                            Note: {timesheet.contractor_confirmation_note}
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Payment Confirmation Section - shows batch details when paid */}
                  {isPaid && (
                    <div className="financial-section bg-purple-50 rounded-lg p-4 border border-purple-200">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-semibold text-purple-800">Payment Confirmation</h4>
                        {paymentBatch && paymentBatch.timesheets?.length > 1 ? (
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-purple-200 text-purple-700">
                            Batch Payment
                          </span>
                        ) : !loadingBatch ? (
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-200 text-gray-600">
                            Single Payment
                          </span>
                        ) : null}
                      </div>

                      {loadingBatch ? (
                        <div className="flex items-center gap-2 text-purple-600">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span className="text-sm">Loading payment details...</span>
                        </div>
                      ) : paymentBatch ? (
                        <div className="space-y-3">
                          {/* Transaction Details */}
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-xs font-medium text-gray-400 uppercase">Transaction Reference</p>
                              <p className="text-gray-900 font-mono">{paymentBatch.transaction_ref}</p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-400 uppercase">Payment Date</p>
                              <p className="text-gray-900">
                                {new Date(paymentBatch.created_at).toLocaleDateString('en-AU', {
                                  day: 'numeric',
                                  month: 'short',
                                  year: 'numeric'
                                })}
                              </p>
                            </div>
                          </div>

                          {/* Batch Total */}
                          <div className="pt-2 border-t border-purple-200">
                            <p className="text-xs font-medium text-gray-400 uppercase mb-1">Batch Total</p>
                            <p className="text-lg font-bold text-purple-700">{formatCurrency(paymentBatch.total_amount)}</p>
                          </div>

                          {/* Proof of Payment */}
                          {paymentBatch.proof_url && (
                            <div className="pt-2 border-t border-purple-200">
                              <p className="text-xs font-medium text-gray-400 uppercase mb-2">Proof of Payment</p>
                              <img
                                src={paymentBatch.proof_url}
                                alt="Proof of payment"
                                className="max-h-32 object-contain rounded border border-purple-200"
                                crossOrigin="anonymous"
                              />
                            </div>
                          )}

                          {/* Jobs in this Batch */}
                          {paymentBatch.timesheets && paymentBatch.timesheets.length > 1 && (
                            <div className="pt-2 border-t border-purple-200">
                              <p className="text-xs font-medium text-gray-400 uppercase mb-2">
                                Jobs in this Payment ({paymentBatch.timesheets.length})
                              </p>
                              <div className="space-y-1.5">
                                {paymentBatch.timesheets.map((ts) => {
                                  // Calculate earnings using the standard calculation (min 1hr rule)
                                  const instanceForCalc = {
                                    timesheet: [ts],
                                    overtime_request: ts.job_instance?.overtime_request
                                  };
                                  const tsJob = ts.job_instance?.job;
                                  const tsFinancials = calculateInstanceFinancials(instanceForCalc, tsJob);
                                  const tsCalculatedAmount = (tsFinancials.payoutToContractor || 0) +
                                    (tsFinancials.hasOvertimeApproved ? (tsFinancials.overtimePayoutToContractor || 0) : 0);

                                  return (
                                    <div
                                      key={ts.id}
                                      className="flex items-center justify-between py-1.5 px-2 bg-white rounded border border-purple-100"
                                    >
                                      <div className="flex items-center gap-2">
                                        <Briefcase className="w-3.5 h-3.5 text-purple-500" />
                                        <span className="text-sm text-gray-900">
                                          {ts.job_instance?.job?.job_name || 'Unknown Job'}
                                        </span>
                                        <span className="text-xs text-gray-500">
                                          ({ts.contractor?.full_name || 'Unknown'})
                                        </span>
                                      </div>
                                      <span className="text-sm font-medium text-gray-900">
                                        {formatCurrency(tsCalculatedAmount)}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-600">
                          This job was paid individually (not part of a batch payment).
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="border-t-2 border-gray-200 pt-6 mt-8">
              <div className="flex justify-between text-xs text-gray-400 mb-6">
                <div>
                  <p>Created: {formatDate(job?.createdAt, preferences.date_format)}</p>
                  {job?.updatedAt && <p>Last Updated: {formatDate(job?.updatedAt, preferences.date_format)}</p>}
                </div>
                <div className="text-right">
                  <p>Invoice generated</p>
                  <p>{new Date().toLocaleString('en-AU')}</p>
                </div>
              </div>

              {/* Powered by Mervo */}
              <div className="flex items-center justify-center gap-2 py-4">
                <span className="text-xs text-gray-400">Powered by</span>
                <div className="w-5 h-5 bg-gradient-to-br from-orange-500 to-orange-600 rounded flex items-center justify-center text-white text-xs font-bold">
                  M
                </div>
                <span className="text-xs text-gray-500 font-semibold">Mervo</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
